/**
 * Textarea — label + multiline input for free-text reasons (motiu obligatori on
 * suspend / soft-delete). Token-driven; mirrors the Field styling.
 */

import React from 'react';
import { useTheme } from '@obrava/ui';

export interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'style'> {
  readonly label: string;
  readonly error?: string | undefined;
  readonly hint?: string | undefined;
  readonly containerStyle?: React.CSSProperties | undefined;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  props,
  ref,
): React.ReactElement {
  const { label, error, hint, containerStyle, id, disabled = false, onFocus, onBlur, ...rest } =
    props;

  const theme = useTheme();
  const [focused, setFocused] = React.useState(false);
  const reactId = React.useId();
  const fieldId = id ?? reactId;
  const describedById = `${fieldId}-desc`;

  const hasError = Boolean(error);
  const borderColor = hasError
    ? theme.color.danger
    : focused
      ? theme.color.borderFocus
      : theme.color.border;

  const labelStyle: React.CSSProperties = {
    fontFamily: theme.typography.family.sans,
    fontSize: theme.typography.size.footnote,
    fontWeight: theme.typography.weight.semibold,
    color: theme.color.textPrimary,
    marginBottom: theme.spacing.space[3],
  };

  const textareaStyle: React.CSSProperties = {
    boxSizing: 'border-box',
    width: '100%',
    minHeight: theme.spacing.size.fieldHeightPlanta,
    padding: theme.spacing.space[4],
    backgroundColor: disabled ? theme.color.surfaceSunken : theme.color.surface,
    color: disabled ? theme.color.textDisabled : theme.color.textPrimary,
    border: `1px solid ${borderColor}`,
    borderRadius: theme.radii.radius.field,
    fontFamily: theme.typography.family.sans,
    fontSize: theme.typography.size.body,
    lineHeight: theme.typography.lineHeight.normal,
    outline: 'none',
    resize: 'vertical',
    boxShadow: focused && !hasError ? theme.radii.shadow.focusRing : 'none',
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
  };

  const messageStyle: React.CSSProperties = {
    fontFamily: theme.typography.family.sans,
    fontSize: theme.typography.size.caption,
    color: hasError ? theme.color.dangerText : theme.color.textTertiary,
    marginTop: theme.spacing.space[2],
  };

  const message = error ?? hint;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', ...containerStyle }}>
      <label htmlFor={fieldId} style={labelStyle}>
        {label}
      </label>
      <textarea
        {...rest}
        ref={ref}
        id={fieldId}
        disabled={disabled}
        aria-invalid={hasError}
        aria-describedby={message ? describedById : undefined}
        style={textareaStyle}
        onFocus={(e) => {
          setFocused(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}
      />
      {message ? (
        <span id={describedById} style={messageStyle}>
          {message}
        </span>
      ) : null}
    </div>
  );
});
